/**
 * V253: Pick Best Photo Source - Never return dead blob URLs
 * 
 * This helper ensures we never select a blob URL unless it's confirmed to exist.
 * If blob is missing or dead, immediately falls back to Cloudinary URL.
 */

import { getProfileUrlForKey } from './objectUrl';
import { profilePhotoUrlKey, getProfilePhotoCachePayload } from './profilePhotoKeys';

export interface PickBestPhotoSrcOptions {
  uiOverride?: string | null;
  photoStoreUrl?: string | null; // PhotoStore URL (can be blob or remote)
  cachePayload?: { kind: 'blob' | 'url'; blobKey?: string; url?: string } | null;
  remoteUrl?: string | null;
  userId: string;
}

/**
 * V253: Pick the best photo source, never returning dead blob URLs
 * 
 * Priority:
 * 1. uiOverride if it's a valid URL (not blob, or blob is confirmed live)
 * 2. photoStoreUrl if it's not a blob, or blob is confirmed live
 * 3. cachePayload.kind === 'url' -> use url
 * 4. cachePayload.kind === 'blob' -> resolve to objectURL ONLY if PhotoStore has live objectURL
 * 5. remoteUrl from DB
 * 
 * Returns null if no valid source found
 */
export function pickBestPhotoSrc(options: PickBestPhotoSrcOptions): string | null {
  const { uiOverride, photoStoreUrl, cachePayload, remoteUrl, userId } = options;

  // Helper to verify blob URL is live
  const verifyBlobIsLive = (blobUrl: string): boolean => {
    const key = profilePhotoUrlKey(userId);
    const liveObjectUrl = getProfileUrlForKey(key);
    return liveObjectUrl === blobUrl;
  };

  // Priority 1: UI Override (optimistic update during replacement)
  if (uiOverride) {
    // If it's a blob, verify it's still live
    if (uiOverride.startsWith('blob:')) {
      if (verifyBlobIsLive(uiOverride)) {
        return uiOverride;
      } else {
        // Blob is dead - reject it
        if (import.meta.env.DEV) {
          console.warn('⚠️ V253: BLOB_REJECTED_NOT_LIVE', { 
            blobKey: profilePhotoUrlKey(userId),
            overrideUrl: uiOverride.substring(0, 50),
          });
        }
        // Fall through to next priority
      }
    } else {
      // Not a blob, safe to use
      return uiOverride;
    }
  }

  // Priority 2: PhotoStore URL (can be blob or remote)
  if (photoStoreUrl) {
    if (photoStoreUrl.startsWith('blob:')) {
      if (verifyBlobIsLive(photoStoreUrl)) {
        return photoStoreUrl;
      } else {
        // Blob is dead - reject it
        if (import.meta.env.DEV) {
          console.warn('⚠️ V253: BLOB_REJECTED_NOT_LIVE', { 
            blobKey: profilePhotoUrlKey(userId),
            photoStoreUrl: photoStoreUrl.substring(0, 50),
          });
        }
        // Fall through to next priority
      }
    } else {
      // Not a blob, safe to use
      return photoStoreUrl;
    }
  }

  // Priority 3: Cache payload (structured)
  if (cachePayload) {
    if (cachePayload.kind === 'url' && cachePayload.url) {
      // Canonical URL - always safe
      return cachePayload.url;
    }
    
    if (cachePayload.kind === 'blob' && cachePayload.blobKey) {
      // Blob entry - verify object URL exists
      const liveObjectUrl = getProfileUrlForKey(cachePayload.blobKey);
      
      if (liveObjectUrl) {
        return liveObjectUrl;
      } else {
        // Blob is dead - reject it
        if (import.meta.env.DEV) {
          console.warn('⚠️ V253: BLOB_REJECTED_NOT_LIVE', { 
            blobKey: cachePayload.blobKey,
          });
        }
        // Fall through to next priority
      }
    }
  }

  // Priority 4: Remote URL from DB
  if (remoteUrl && typeof remoteUrl === 'string' && remoteUrl.startsWith('http')) {
    return remoteUrl;
  }

  return null;
}

